import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  [
    "inline-flex items-center gap-1",
    "rounded-md border px-2 py-0.5",
    "text-xs font-medium",
    "transition-colors duration-150",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary/10 text-primary",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-red-200 bg-red-50 text-red-700",
        outline:
          "border-border text-foreground bg-transparent",
        // Semantic status variants (B2B pattern)
        success:
          "border-emerald-200 bg-emerald-50 text-emerald-700",
        warning:
          "border-amber-200 bg-amber-50 text-amber-700",
        error:
          "border-red-200 bg-red-50 text-red-700",
        info:
          "border-blue-200 bg-blue-50 text-blue-700",
        // Production status variants
        processing:
          "border-violet-200 bg-violet-50 text-violet-700",
        pending:
          "border-slate-200 bg-slate-50 text-slate-600",
        shipped:
          "border-emerald-200 bg-emerald-50 text-emerald-700",
        // Muted variant for subtle labels
        muted:
          "border-transparent bg-muted text-muted-foreground",
        // Production order soft pastel badges
        items:
          "border-transparent bg-[hsl(var(--badge-items-bg))] text-[hsl(var(--badge-items-text))] font-medium rounded-full",
        shipments:
          "border-transparent bg-[hsl(var(--badge-shipments-bg))] text-[hsl(var(--badge-shipments-text))] font-medium rounded-full",
        orderTotal:
          "border-[hsl(var(--production-accent))]/30 bg-[hsl(var(--metric-total))] text-[hsl(160,60%,25%)] font-semibold px-3 py-1.5",
        // BLU-373 PR2: terminal "paid" status — filled emerald (vs success
        // which is soft emerald) so it visually reads as "closed and locked"
        // rather than "delivered". Pair with a Lock icon at call sites.
        paid:
          "border-emerald-700 bg-emerald-600 text-white",
        // Legacy variants for backwards compatibility
        deleted:
          "border-red-200 bg-red-50 text-red-700 line-through opacity-70",
      },
      size: {
        default: "px-2 py-0.5 text-xs",
        sm: "px-1.5 py-px text-[10px]",
        lg: "px-2.5 py-1 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Optional dot indicator */
  dot?: boolean
  /** Dot color class - defaults to current text color */
  dotColor?: string
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, dot, dotColor, children, ...props }, ref) => {
    return (
      <span ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props}>
        {dot && (
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full shrink-0",
              dotColor || "bg-current"
            )}
            aria-hidden="true"
          />
        )}
        {children}
      </span>
    )
  }
)
Badge.displayName = "Badge"

export { Badge, badgeVariants }
